import {
  ColumnTypes,
  editableComponents,
  IEditableCellProps,
} from "@/containers/ProjectTemplate";
import { nameof } from "@/helpers";
import {
  IFormFieldOptions,
  IMilestoneTemplate,
  INotificationRole,
} from "@/interfaces";
import { useGetFormLookupsQuery, useGetProjectRoleQuery } from "@/services";
import { FieldType } from "@/types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faPlusCircle, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Table } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { v4 } from "uuid";
import {
  clearRoleDependancies,
  filterRegionAndFunctionalArea,
  isValidRole,
} from "../helpers";

interface IProps {
  milestoneTemplate: IMilestoneTemplate;
  updateMilestone(data: Partial<IMilestoneTemplate>): void;
  setIsValid(isValid: object): void;
}

export const TemplateNotificationRoles = ({
  milestoneTemplate,
  updateMilestone,
  setIsValid,
}: IProps) => {
  const intl = useIntl();
  const { data: { intake } = {} } = useGetFormLookupsQuery();
  const { data: roles } = useGetProjectRoleQuery({});
  const [notificationRoles, setNotificationRoles] = useState<
    INotificationRole[]
  >([]);
  const [roleOptions, setRoleOptions] = useState<IFormFieldOptions>({});

  useEffect(() => {
    if (!roles) return;

    const dictionary: IFormFieldOptions = {};
    roles?.forEach(({ id, name }: { id: string; name: string }) => {
      dictionary[id] = { id: "", fallback: name };
    });

    setRoleOptions(dictionary);
  }, [roles]);

  useEffect(() => {
    if (!milestoneTemplate) return;
    if (!roles) return;

    const filteredRoles = milestoneTemplate.notification?.roles.map((x) =>
      filterDependancies(x)
    );
    setNotificationRoles(filteredRoles);
  }, [roles, milestoneTemplate]);

  const updateModalState = (updatedRoles: INotificationRole[]): void => {
    isValidRoles(updatedRoles);
    setNotificationRoles(updatedRoles);
    updateMilestone({
      notification: {
        ...milestoneTemplate.notification,
        roles: updatedRoles,
      },
    });
  };

  const isValidRoles = (updatedRoles: INotificationRole[]): void => {
    setIsValid((prev: object) => ({
      ...prev,
      role: isValidRole(updatedRoles),
    }));
  };

  const handleAdd = (): void => {
    const newRole = [
      {
        id: v4(),
      } as INotificationRole,
    ];

    const updatedRoles = newRole.concat(notificationRoles);

    updateModalState(updatedRoles);
  };

  const handleDelete = (id: string): void => {
    const updatedRoles = notificationRoles.filter((group) => group.id !== id);

    updateModalState(updatedRoles);
  };

  const handleUpdate = (row: INotificationRole): void => {
    const updatedRoles = [...notificationRoles];
    const index = updatedRoles.findIndex((role) => row.id === role.id);
    const item = updatedRoles[index];
    const updatedRow = clearRoleDependancies(roles, item, row);
    const filteredRow = filterDependancies(updatedRow);

    updatedRoles.splice(index, 1, {
      ...item,
      ...filteredRow,
    });

    updateModalState(updatedRoles);
  };

  const filterDependancies = (
    notificationRole: INotificationRole
  ): INotificationRole => {
    if (!roles) return notificationRole;
    if (!notificationRole) return notificationRole;

    const filtered = filterRegionAndFunctionalArea(
      roles,
      intake,
      notificationRole
    );

    const filteredNotificationRole = { ...notificationRole };

    if (filtered.regions) {
      filteredNotificationRole.regionOptions = filtered.regions;
    }

    if (filtered.functionalAreas) {
      filteredNotificationRole.functionalAreaOptions = filtered.functionalAreas;
    }

    return filteredNotificationRole;
  };

  const defaultColumns: (ColumnTypes[number] & {
    type: FieldType | undefined;
    dataIndex: string;
    required?: boolean;
    editable?: boolean;
    options?: IFormFieldOptions;
    optionsIndex?: string;
    showSearch?: boolean;
    placeholder?: string;
  })[] = [
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.role" }),
      dataIndex: nameof<INotificationRole>("roleId"),
      key: "role",
      width: "300px",
      type: "DropDown",
      required: true,
      editable: true,
      showSearch: true,
      options: roleOptions,
    },
    {
      title: intl.formatMessage({ id: "milestoneTemplates.columns.region" }),
      dataIndex: nameof<INotificationRole>("region"),
      key: "region",
      width: "150px",
      type: "DropDown",
      required: true,
      editable: true,
      optionsIndex: nameof<INotificationRole>("regionOptions"),
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.functionalArea",
      }),
      dataIndex: nameof<INotificationRole>("functionalArea"),
      key: "functionalArea",
      width: "175px",
      type: "DropDown",
      required: true,
      editable: true,
      optionsIndex: nameof<INotificationRole>("functionalAreaOptions"),
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.productCategory",
      }),
      dataIndex: nameof<INotificationRole>("defaultForProductCategories"),
      key: "productCategory",
      width: "500px",
      type: "MultiSelectDropDown",
      required: true,
      editable: true,
      fixed: true,
      options: intake?.productCategory,
      placeholder: intl.formatMessage({ id: "general.all" }),
    },
    {
      title: intl.formatMessage({
        id: "milestoneTemplates.columns.notApplicable",
      }),
      dataIndex: nameof<INotificationRole>("canSetNotApplicable"),
      key: "canSetNotApplicable",
      width: "85px",
      type: "Checkbox",
      editable: true,
    },
    {
      title: (
        <Button
          data-test-id="milestone-template-notification-roles-add"
          type="link"
          onClick={handleAdd}
          icon={<FontAwesomeIcon icon={faPlusCircle as IconProp} size="xl" />}
        />
      ),
      type: undefined,
      editable: false,
      dataIndex: nameof<INotificationRole>("id"),
      key: "delete",
      render: (id: string) => (
        <Button
          data-test-id="milestone-template-notification-roles-delete"
          type="link"
          onClick={(): void => handleDelete(id)}
          icon={<FontAwesomeIcon icon={faTrash as IconProp} color="red" />}
        />
      ),
    },
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: INotificationRole) =>
        ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          type: col.type,
          required: col.required,
          options: col.options,
          optionsIndex: col.optionsIndex,
          showSearch: col.showSearch,
          handleSave: handleUpdate,
          placeholder: col.placeholder,
        } as IEditableCellProps<INotificationRole>),
    };
  });

  return (
    <Table
      components={editableComponents}
      style={{ margin: "-12px 0px" }}
      tableLayout="fixed"
      className="w-full"
      size="small"
      columns={columns as ColumnTypes}
      dataSource={notificationRoles}
      rowKey="id"
      pagination={false}
    />
  );
};
